import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import toast from 'react-hot-toast';

const todayISO = () => new Date().toISOString().slice(0, 10);

const CashBankPosition = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  const runReport = async () => {
    try {
      setLoading(true);
      setUnavailable(false);
      const params = {};
      if (asOfDate) params.as_of_date = asOfDate;
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      const response = await accountsApi.get('/financial-statements/cash-bank', { params });
      setData(response.data.cash_bank_position);
    } catch (err) {
      // This report's known failure mode is a database column that may not
      // be set up yet (chart_of_accounts.is_cash_or_bank) — the backend
      // reports that the same way as any other server error, so we can't
      // tell them apart from the response alone. Show a specific message
      // rather than a generic toast, since that's the far more likely cause.
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout title="Cash and Bank Position">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">As Of</label>
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
            <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Cost Centers</option>
              {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : unavailable ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <p className="text-amber-800 font-medium">This report isn't available yet.</p>
          <p className="text-amber-700 text-sm mt-1">
            It likely means the Cash &amp; Bank classification hasn't been set up in the database yet. Contact your administrator.
          </p>
        </div>
      ) : !data ? null : (
        <>
          <div className={`rounded-xl p-4 mb-4 flex items-center justify-between ${data.validation.reconciled_against_ledger ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <span className={`font-semibold ${data.validation.reconciled_against_ledger ? 'text-green-700' : 'text-red-700'}`}>
              {data.validation.reconciled_against_ledger ? '✓ Reconciled' : '✗ Mismatch'}
            </span>
            <span className="font-semibold text-gray-800">Total: {data.total.toFixed(2)}</span>
          </div>

          {data.warnings?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4 space-y-1">
              {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}

          <div className="bg-white rounded-xl shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Code</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Name</th>
                  <th className="text-right px-4 py-3 text-gray-600 font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-8 text-gray-500">No cash/bank accounts found</td></tr>
                ) : data.accounts.map(a => (
                  <tr key={a.id}
                    onClick={() => navigate(`/ledgers?account_id=${a.id}&to_date=${asOfDate}`)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-2 font-mono text-gray-700">{a.account_code}</td>
                    <td className="px-4 py-2 font-medium text-gray-800">{a.account_name}</td>
                    <td className="px-4 py-2 text-right text-gray-800">{a.balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  );
};

export default CashBankPosition;
